import {
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Button,
  TableRow,
  Typography,
  Tooltip,
  makeStyles
} from '@material-ui/core/';
import withStyles from '@material-ui/core/styles/withStyles';
import { groupBy, toArray, findLast, get, cloneDeep } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react';
import {
  fetchEnd,
  fetchStart,
  FormDataConsumer,
  ReferenceInput,
  required,
  AutocompleteInput,
  TextInput,
  Loading
} from 'react-admin';
import { useForm, useFormState, Field } from 'react-final-form';
import { connect } from 'react-redux';

import FileField from '../../../../core/components/forked/field/FileField';
import FileInput from '../../../../core/components/forked/input/FileInput';
import GridPopoverWithCloseButton from '../../../../core/components/grid/GridPopoverWithCloseButton';
import RichTextInput from '../../../../core/components/inputs/RichTextInput';
import FieldTitleWithListPopover from '../../../../core/components/inputs/FieldTitleWithListPopover';
import TenantConfigurationApi from '../../../../core/apis/TenantConfigurationApi';
import dataProvider from '../../../../core/providers/IccUserDataProvider';
import { ChevronDownIcon } from '../../../../core/themes/icons/ChevronDownIcon';
import { ChevronRightIcon } from '../../../../core/themes/icons/ChevronRightIcon';
import CheckBoxField from '../../../../core/components/CheckBoxField';
import {
  isValidNoneOrMultipleCommaSeparatedEmails,
  getSubjectVariables,
  getBodyVariables
} from '../../../../core/utils/index';
import {
  isValidEmailRegex,
  fieldLengthValidator
} from '../../../../core/utils/index';
import { mailTemplateTypes, mupVariables } from './EmailFormLookups';
import EmailFormStyles from './EmailFormStyles';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import FundApi from '../../../../core/apis/FundApi';

const maxSingleAddressLength = 100;

const removeBlanks = value => {
  if (value && value.length > 0) {
    return value.replace(/\s/g, '');
  }
  return value;
};

const useStyles = makeStyles({
  fromListMessage: {
    fontSize: '0.875rem',
    textAlign: 'left',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    fontWeight: '400',
    paddingTop: '1rem',
    fontStyle: 'italic'
  }
});

const validateEmails = field => {
  var message;

  if (field === '') {
    return;
  }

  if (!isValidNoneOrMultipleCommaSeparatedEmails(field) === true) {
    message =
      'Please enter a valid email address or multiple email addresses separated by semicolon';
  } else if (field !== undefined) {
    const splittedEmails = field.split(';');
    splittedEmails[0] &&
      splittedEmails.forEach(email => {
        if (email.length > maxSingleAddressLength) {
          message =
            'At least one email address contains more than ' +
            maxSingleAddressLength +
            ' characters.';
        } else if (!isValidEmailRegex(email)) {
          message =
            'Please enter a valid email address or multiple email addresses separated by semicolon';
        }
      });
  }
  return message;
};

const validateEmail = field => {
  var message;
  if (!isValidEmailRegex(field) === true) {
    message = 'Please enter a valid email address';
  } else if (field && field.length > maxSingleAddressLength) {
    message =
      'Email address should not contain more than ' +
      maxSingleAddressLength +
      ' characters.';
  }
  return message;
};

const getFullName = user => {
  const firstName = get(user, 'firstName', get(user, 'firstname', ''));
  const lastName = get(user, 'lastName', get(user, 'lastname', ''));
  return `${firstName} ${lastName}`;
};

const getCompositeId = (user, ids) => {
  let compositeId = '';
  ids.forEach(id => (compositeId = compositeId + user[id] + ':'));
  return compositeId;
};

const checkChoiceIsSelected = (selectedItems, choice, recipientId) => {
  const choiceCompoId = getCompositeId(choice, recipientId);
  return selectedItems.findIndex(
    item => getCompositeId(item, recipientId) === choiceCompoId
  );
};

const checkCcChoiceIsSelected = (selectedItems, choice, cc, recipientId) => {
  const index = checkChoiceIsSelected(selectedItems, choice, recipientId);
  if (index >= 0) {
    return selectedItems[index].ccEmails.findIndex(item => item === cc);
  } else {
    return -1;
  }
};

const PopoverToContent = withStyles(EmailFormStyles)(
  ({ choices, classes, text, variantButton, colorButton, ...rest }) => {
    const { selectedItems } = rest;
    const [open, setOpen] = useState(false);

    const { rowCount = 0, selectedRowsCount = 0 } = useMemo(() => {
      const rows = [];
      const selectedRows = [];

      choices.forEach(choice => {
        rows.push(choice.email);
        if (Array.isArray(choice.ccEmails)) {
          choice.ccEmails.forEach(cc => {
            rows.push(cc);
          });
        }
      });

      selectedItems.forEach(item => {
        selectedRows.push(item.email);
        if (Array.isArray(item.ccEmails)) {
          item.ccEmails.forEach(cc => {
            selectedRows.push(cc);
          });
        }
      });

      return { rowCount: rows.length, selectedRowsCount: selectedRows.length };
    }, [choices, selectedItems]);

    return (
      <GridPopoverWithCloseButton
        text={text}
        open={open}
        openModal={() => setOpen(true)}
        variantButton={variantButton}
        colorButton={colorButton}
        classes={{ button: classes.buttonClass, paper: classes.paper }}
        options={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }}
        popoverContent={
          <RecipientsList
            classes={classes}
            handleClose={() => setOpen(false)}
            choices={choices}
            rowCount={rowCount}
            selectedRowsCount={selectedRowsCount}
            {...rest}
          />
        }
      />
    );
  }
);

const PopoverToContentFromList = withStyles(EmailFormStyles)(
  ({
    choices,
    classes,
    text,
    variantButton,
    colorButton,
    fundsData,
    defaultFromEmail,
    ...rest
  }) => {
    const cssClasses = useStyles();

    const { selectedItems } = rest;
    const [openFromList, setOpenFromList] = useState(false);
    const [fromList, setFromList] = useState();

    const openFromListDialog = () => {
      generateFromList();
      setOpenFromList(true);
    };

    const generateFromList = async () => {
      const uniqueItemsMap = new Map();
      selectedItems.forEach(item => {
        if (!uniqueItemsMap.has(item.fundName)) {
          uniqueItemsMap.set(item.fundName, item);
        }
      });
      const tableRows = Array.from(uniqueItemsMap.values()).map(
        (item, index) => (
          <TableRow key={`${item.email}-${item.fundId}`}>
            <TableCell>{item.fundName}</TableCell>
            <TableCell>{getEmailByFundId(item.fundId)}</TableCell>
          </TableRow>
        )
      );
      setFromList(tableRows);
    };

    const getEmailByFundId = fundId => {
      if (fundsData && fundsData.pageData && fundsData.pageData.length > 0) {
        const fund = fundsData.pageData.find(fund => fund.id === fundId);
        return fund && fund.emailAddresses && fund.emailAddresses.length > 0
          ? fund.emailAddresses[0]
          : defaultFromEmail;
      }
      return defaultFromEmail;
    };

    return (
      <GridPopoverWithCloseButton
        text={'Fund From Address'}
        open={openFromList}
        openModal={() => openFromListDialog()}
        variantButton={variantButton}
        colorButton={colorButton}
        classes={{ button: classes.buttonClass, paper: classes.paper }}
        options={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }}
        popoverContent={
          <>
            <div>
              <Table
                stickyHeader
                aria-label="sticky table"
                classes={{ root: classes.tableRoot }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell classes={{ root: classes.rootCell }}>
                      Fund:
                    </TableCell>
                    <TableCell classes={{ root: classes.rootCell }}>
                      From Email Address:
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{fromList}</TableBody>
              </Table>
            </div>
            <div className={cssClasses.fromListMessage}>
              For this email template, the "From" address is selected from the
              Fund Contact email if it exists.
              <br />
              For recipients linked to multiple Funds, the sender's email will
              be the "From" address.
            </div>
            <div className={classes.root}>
              <Button
                onClick={() => setOpenFromList(false)}
                variant="outlined"
                color="primary"
              >
                Close
              </Button>
            </div>
          </>
        }
      />
    );
  }
);

/**
 * Table of recipients list
 */
const RecipientsList = ({ classes, handleClose, ...rest }) => {
  return (
    <>
      <div>
        <Table
          stickyHeader
          aria-label="sticky table"
          classes={{ root: classes.tableRoot }}
        >
          <TableHead>
            <TableRow>
              <TableCell classes={{ root: classes.rootCell }}>Entity</TableCell>
              <TableCell classes={{ root: classes.rootCell }}>To:</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <RecipientsListRow classes={classes} {...rest} />
          </TableBody>
        </Table>
      </div>
      <div className={classes.root}>
        <Button onClick={handleClose} variant="outlined" color="primary">
          Apply
        </Button>
      </div>
    </>
  );
};

/**
 * Displaying each line of recipient orderedByInvestor
 */
const RecipientsListRow = ({
  classes,
  choices,
  rowCount,
  selectedRowsCount,
  form,
  ...rest
}) => {
  const groupedChoices =
    rest.groupBy && Object.values(groupBy(choices, rest.groupBy));

  const onSelectAllClick = useCallback(() => {
    if (selectedRowsCount === rowCount) {
      form.change('selectedItems', []);
    } else {
      form.change('selectedItems', cloneDeep(choices));
    }
  }, [form, selectedRowsCount, rowCount, choices]);

  return (
    <>
      <TableRow>
        <TableCell>
          <Checkbox
            indeterminate={
              selectedRowsCount > 0 && selectedRowsCount < rowCount
            }
            checked={selectedRowsCount === rowCount && rowCount > 0}
            onClick={onSelectAllClick}
            className={classes.checkbox}
          />
          Select All
        </TableCell>
        <TableCell />
      </TableRow>
      {groupedChoices.map((rowItems, index) => {
        const groupName = get(rowItems[0], rest.groupLabel, '');
        return (
          <ExpandedInformations
            rowItems={rowItems}
            key={groupName || index}
            classes={classes}
            groupName={groupName}
            form={form}
            {...rest}
          />
        );
      })}
    </>
  );
};

const emptySubject = value => {
  const subjectValid = !!value.trim();

  if (!subjectValid) {
    return `Subject can't be empty space(s)`;
  }
};

/**
 * When we drop down information per investor
 */
const ExpandedInformations = ({
  rowItems,
  classes,
  selectedItems,
  form,
  groupName,
  recipientId,
  ...rest
}) => {
  const [expand, setExpand] = useState(false);

  const handleClickCheckbox = useCallback(
    user => {
      const array = cloneDeep(selectedItems);
      const index = checkChoiceIsSelected(selectedItems, user, recipientId);

      if (index >= 0) {
        array.splice(index, 1);
      } else {
        if (Array.isArray(user.ccEmails)) {
          array.push({ ...user, ccEmails: [] });
        } else {
          array.push(user);
        }
      }
      form.change('selectedItems', array);
    },
    [selectedItems, form, recipientId]
  );

  const handleCcClickCheckbox = useCallback(
    (user, cc) => {
      const array = cloneDeep(selectedItems);
      const index = checkChoiceIsSelected(selectedItems, user, recipientId);

      if (index >= 0) {
        const ccIndex = checkCcChoiceIsSelected(
          selectedItems,
          user,
          cc,
          recipientId
        );
        if (ccIndex >= 0) {
          array[index].ccEmails.splice(ccIndex, 1);
        } else {
          array[index].ccEmails.push(cc);
        }
      } else {
        array.push({ ...user, ccEmails: [cc] });
      }
      form.change('selectedItems', array);
    },
    [selectedItems, form, recipientId]
  );

  return (
    <React.Fragment>
      <TableRow>
        <TableCell classes={{ root: classes.tableCell }}>
          <Grid container wrap="nowrap" spacing={1}>
            <Grid
              item
              onClick={() => setExpand(!expand)}
              classes={{ item: classes.arrowGroupGridItem }}
            >
              {expand ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </Grid>
            <Grid item xs={11}>
              <Typography variant="body1" component="div">
                {groupName}
              </Typography>
            </Grid>
          </Grid>
        </TableCell>
        <TableCell />
      </TableRow>
      {rowItems.map((user, index) => {
        return (
          <React.Fragment key={index}>
            {expand && (
              <React.Fragment>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      onClick={() => handleClickCheckbox(user)}
                      className={classes.checkbox}
                      checked={
                        checkChoiceIsSelected(
                          selectedItems,
                          user,
                          recipientId
                        ) >= 0
                      }
                    />
                    {getFullName(user)}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                </TableRow>
                {user.ccEmails &&
                  user.ccEmails.map(cc => (
                    <TableRow key={cc}>
                      <TableCell>
                        <Checkbox
                          onClick={() => handleCcClickCheckbox(user, cc)}
                          checked={
                            checkCcChoiceIsSelected(
                              selectedItems,
                              user,
                              cc,
                              recipientId
                            ) >= 0
                          }
                          className={classes.ccCheckbox}
                        />
                        {`cc: ${cc}`}
                      </TableCell>
                      <TableCell>{cc}</TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            )}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

const getSelectedItems = values => {
  return values?.selectedItems ?? [];
};
const fetchTenantConfigurationsData = async (
  setDefaultEmailAddress,
  setManualEmailDefaultFromEmailAddress,
  setIsLoading
) => {
  try {
    const customEmailResponse = await TenantConfigurationApi.get(
      'Administrator_CustomEmailAddress'
    );
    const customEmail = customEmailResponse?.pageData?.[0]?.value;

    if (customEmail) {
      setDefaultEmailAddress(customEmail);
    } else {
      const fallbackEmailResponse = await TenantConfigurationApi.get(
        'Administrator_Email'
      );
      const fallbackEmail = fallbackEmailResponse?.pageData?.[0]?.value;
      if (fallbackEmail) {
        setDefaultEmailAddress(fallbackEmail);
      }
    }

    const manualEmailResponse = await TenantConfigurationApi.get(
      'ManualEmail_Default_From_Email_Address'
    );
    setManualEmailDefaultFromEmailAddress(
      manualEmailResponse?.pageData?.[0]?.value
    );
  } finally {
    setIsLoading(false);
  }
};

const updateFundName = (selectedItems, form) => {
  if (selectedItems.length === 0) {
    form.change('fundname', 'No fund selected');
    return;
  }

  const sameFundSelected =
    findLast(
      selectedItems,
      item => item.fundName !== selectedItems[0].fundName
    ) === undefined;

  if (sameFundSelected || selectedItems.length < 2) {
    form.change('fundname', selectedItems[0].fundName);
  } else {
    form.change('fundname', 'Multiple Funds');
  }
};

const fetchFundsDataAsync = async (
  useFundEmail,
  selectedFundIdsKey,
  setFundsData,
  notifyServerError
) => {
  if (!useFundEmail) {
    setFundsData([]);
    return;
  }

  if (!selectedFundIdsKey) {
    setFundsData([]);
    return;
  }

  try {
    const uniqueFundIds = selectedFundIdsKey.split(',');
    const fundsData = await FundApi.getFundsByIds(uniqueFundIds);
    setFundsData(fundsData);
  } catch (error) {
    notifyServerError(error);
  }
};

const fetchAliasUdfs = async (userId, form, notifyServerError) => {
  fetchStart();
  try {
    const { data } = await dataProvider('GET_ONE', 'aliasUdfs', { id: userId });
    if (data) {
      if (data.emailAlias) {
        form.change('alias', data.emailAlias);
      }
      if (data.emailAliasReplyTo) {
        form.change('aliasTo', data.emailAliasReplyTo);
      }
    }
  } catch (error) {
    notifyServerError(error);
  } finally {
    fetchEnd();
  }
};

const isManualEmailOverrideDisabled = (
  useManualEmailFrom,
  manualEmailDefaultFromEmailAddress
) =>
  useManualEmailFrom &&
  manualEmailDefaultFromEmailAddress !==
    'Logged-In_User_With_Override_From_Email';

const getVariablesForType = (variableType, isMupChild, getVariablesFn) =>
  isMupChild
    ? getVariablesFn(variableType).concat(mupVariables)
    : getVariablesFn(variableType);

const FromField = ({
  useFundEmail,
  isLoading,
  useManualEmailFrom,
  manualEmailDefaultFromEmailAddress,
  defaultEmailAddress,
  variant,
  classes,
  choices,
  open,
  setOpen,
  textButton,
  selectedItems,
  form,
  groupBy,
  groupLabel,
  recipientId,
  fundsData,
  userProfile
}) => {
  if (useFundEmail) {
    return (
      <>
        <span className={classes.labelCustom}>From:</span>
        <PopoverToContentFromList
          choices={choices}
          open={open}
          text={textButton}
          colorButton="primary"
          variantButton="outlined"
          handleClick={() => setOpen(!open)}
          selectedItems={selectedItems}
          form={form}
          groupBy={groupBy}
          groupLabel={groupLabel}
          recipientId={recipientId}
          fundsData={fundsData}
          defaultFromEmail={userProfile.email}
        />
      </>
    );
  }

  if (isLoading) {
    return <></>;
  }

  const isOverrideDisabled = isManualEmailOverrideDisabled(
    useManualEmailFrom,
    manualEmailDefaultFromEmailAddress
  );
  const isLoggedInUserFromEmail =
    manualEmailDefaultFromEmailAddress ===
      'Logged-In_User_With_Override_From_Email' ||
    manualEmailDefaultFromEmailAddress === 'Logged-In_User_From_Email';

  return (
    <TextInput
      source={useManualEmailFrom && !isLoggedInUserFromEmail ? 'n/a' : 'from'}
      defaultValue={useManualEmailFrom ? defaultEmailAddress : ''}
      label="From:"
      type="email"
      validate={[required(), validateEmail]}
      options={{ autoComplete: 'off' }}
      variant={variant}
      helperText={false}
      disabled={isOverrideDisabled}
    />
  );
};

const TemplateReferenceInput = ({ variant, onTemplateChange }) => (
  <FormDataConsumer>
    {({ formData }) =>
      formData && (
        <ReferenceInput
          label="Email Template"
          source="templateId"
          resource="mailtemplates"
          reference="mailtemplates"
          filter={{ type: formData.type, onlyInfo: true }}
          sort={{ field: 'description', order: 'ASC' }}
          perPage={-1}
          variant={variant}
          allowEmpty
          onChange={event => onTemplateChange(event)}
        >
          <AutocompleteInput
            optionText="description"
            helperText={false}
            options={{
              suggestionsContainerProps: {
                style: {
                  backgroundColor: 'white',
                  border: '1px solid #E0E0E0',
                  boxShadow:
                    ' 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
                }
              }
            }}
          />
        </ReferenceInput>
      )
    }
  </FormDataConsumer>
);

const ToField = ({
  choices,
  variant,
  textButton,
  selectedItems,
  form,
  groupBy,
  groupLabel,
  recipientId,
  open,
  setOpen,
  classes
}) => {
  if (choices) {
    return (
      <Grid item xs={12} className={classes.buttonItem}>
        <span className={classes.labelCustom}>To:</span>
        <PopoverToContent
          choices={choices}
          open={open}
          text={textButton}
          colorButton="primary"
          variantButton="outlined"
          handleClick={() => setOpen(!open)}
          selectedItems={selectedItems}
          form={form}
          groupBy={groupBy}
          groupLabel={groupLabel}
          recipientId={recipientId}
        />
      </Grid>
    );
  }
  return (
    <Grid item xs={12}>
      <TextInput
        source="userEmail"
        label="To:"
        helperText={false}
        type="text"
        options={{ autoComplete: 'off' }}
        variant={variant}
        validate={[required(), validateEmails]}
      />
    </Grid>
  );
};

const syncNotificationMethod = (
  notificationMethod,
  notificationLabel,
  previousNotificationMethod,
  setNotificationLabel,
  setPreviousNotificationMethod,
  form,
  setTemplateDropdownRefreshKey
) => {
  if (previousNotificationMethod !== notificationLabel) {
    setNotificationLabel(notificationMethod);
    setPreviousNotificationMethod(notificationMethod);
    form.reset();
    setTemplateDropdownRefreshKey(prevKey => prevKey + 1);
  }
};
export const handleTemplateChange = async ({
  value,
  form,
  useFundFromEmail,
  setUseFundEmail,
  setLoadingTemplate,
  notifyServerError,
  fetchStart,
  fetchEnd,
  dataProvider
}) => {
  if (!value) {
    setUseFundEmail(false);
    form.change('msgBody', '');
    form.change('subject', '');
    return;
  }

  fetchStart();
  setLoadingTemplate(true);
  try {
    const { data } = await dataProvider('GET_ONE', 'mailtemplates', {
      id: value
    });
    if (data) {
      if (useFundFromEmail) {
        setUseFundEmail(data.useFundEmail);
      } else {
        setUseFundEmail(false);
      }
      form.change('type', data.type);
      form.change('msgBody', data.body);
      form.change('subject', data.subject);
    }
  } catch (error) {
    notifyServerError(error);
  } finally {
    setLoadingTemplate(false);
    fetchEnd();
  }
};

const EmailForm = withStyles(EmailFormStyles)(
  ({
    classes,
    variant,
    choices,
    textButton,
    groupLabel,
    groupBy = null,
    recipientId = ['userId'],
    attachments = true,
    variables = true,
    variableType,
    userProfile = {},
    previousNotificationMethod,
    notificationMethod,
    setPreviousNotificationMethod,
    useFundFromEmail,
    useManualEmailFrom
  }) => {
    const formState = useFormState();
    const form = useForm();
    const subject = useRef(null);
    const selectedItems = useMemo(() => getSelectedItems(formState.values), [
      formState.values
    ]);
    const [open, setOpen] = useState(false);
    const [notificationLabel, setNotificationLabel] = useState(
      notificationMethod
    );
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [useFundEmail, setUseFundEmail] = useState(false);
    const [fundsData, setFundsData] = useState();
    const notifyServerError = useNotifyServerError();
    const isMupChild = sessionStorage.getItem('isMupChild') === 'true';
    const [
      templateDropdownRefreshKey,
      setTemplateDropdownRefreshKey
    ] = useState(0);
    const [
      manualEmailDefaultFromEmailAddress,
      setManualEmailDefaultFromEmailAddress
    ] = useState('Logged-In_User_With_Override_From_Email');
    const [defaultEmailAddress, setDefaultEmailAddress] = useState();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      fetchTenantConfigurationsData(
        setDefaultEmailAddress,
        setManualEmailDefaultFromEmailAddress,
        setIsLoading
      );
    }, []);

    useEffect(() => {
      syncNotificationMethod(
        notificationMethod,
        notificationLabel,
        previousNotificationMethod,
        setNotificationLabel,
        setPreviousNotificationMethod,
        form,
        setTemplateDropdownRefreshKey
      );
    }, [
      notificationMethod,
      notificationLabel,
      previousNotificationMethod,
      setNotificationLabel,
      setPreviousNotificationMethod,
      form,
      setTemplateDropdownRefreshKey
    ]);

    /**
     * When we select a template to fill the ck editor
     */

    const onTemplateChange = useCallback(
      value => {
        handleTemplateChange({
          value,
          form,
          useFundFromEmail,
          setUseFundEmail,
          setLoadingTemplate,
          notifyServerError,
          fetchStart,
          fetchEnd,
          dataProvider
        });
      },
      [
        form,
        useFundFromEmail,
        setUseFundEmail,
        setLoadingTemplate,
        notifyServerError
      ]
    );

    useEffect(() => {
      form.change('from', userProfile.email);
      fetchAliasUdfs(userProfile.userId, form, notifyServerError);

      if (
        formState.values.templateId &&
        previousNotificationMethod === notificationLabel
      ) {
        onTemplateChange(formState.values.templateId);
      }
    }, [
      form,
      formState.values.templateId,
      onTemplateChange,
      userProfile.userId,
      userProfile.email,
      notifyServerError,
      previousNotificationMethod,
      notificationLabel
    ]);

    const selectedFundIdsKey = useMemo(() => {
      const ids = selectedItems.map(item => item.fundId).filter(Boolean);
      const uniqueSortedIds = Array.from(new Set(ids)).sort();
      return uniqueSortedIds.join(',');
    }, [selectedItems]);

    useEffect(() => {
      fetchFundsDataAsync(
        useFundEmail,
        selectedFundIdsKey,
        setFundsData,
        notifyServerError
      );
    }, [selectedFundIdsKey, useFundEmail, notifyServerError]);

    useEffect(() => {
      updateFundName(selectedItems, form);
    }, [form, choices, selectedItems]);

    /**
     * when we want to select a subject variable
     * @param {*} variable
     */
    function handleSelectVariableSubject(variable) {
      let currentValue = toArray(formState.values.subject || '');
      const cursorIndex = subject.current.selectionStart || 0;

      currentValue.splice(cursorIndex, 0, variable.value);
      form.change('subject', currentValue.join(''));
    }

    /**
     * Resetting msg body and mail template when updating type
     */
    const handleChangeType = useCallback(() => {
      form.change('templateId', '');
      form.change('msgBody', '');
    }, [form]);

    const overrideDisabled = isManualEmailOverrideDisabled(
      useManualEmailFrom,
      manualEmailDefaultFromEmailAddress
    );
    const subjectVariables = getVariablesForType(
      variableType,
      isMupChild,
      getSubjectVariables
    );
    const bodyVariables = getVariablesForType(
      variableType,
      isMupChild,
      getBodyVariables
    );

    if (loadingTemplate) return <Loading className={classes.loading} />;

    return (
      <Grid container spacing={2} key={templateDropdownRefreshKey}>
        <Grid item xs={3}>
          <AutocompleteInput
            label="Type"
            source="type"
            choices={mailTemplateTypes}
            allowEmpty
            variant={variant}
            onChange={() => handleChangeType()}
            helperText={false}
            options={{
              suggestionsContainerProps: {
                style: {
                  backgroundColor: 'white',
                  border: '1px solid #E0E0E0',
                  boxShadow:
                    ' 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TemplateReferenceInput
            variant={variant}
            onTemplateChange={onTemplateChange}
          />
        </Grid>
        <Grid item xs={3}>
          {choices && (
            <TextInput
              source="fundname"
              label="Fund Name"
              variant={variant}
              className={classes.disableStyleInput}
              disabled
              helperText={false}
            />
          )}
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={3}>
          <FromField
            useFundEmail={useFundEmail}
            isLoading={isLoading}
            useManualEmailFrom={useManualEmailFrom}
            manualEmailDefaultFromEmailAddress={
              manualEmailDefaultFromEmailAddress
            }
            defaultEmailAddress={defaultEmailAddress}
            variant={variant}
            classes={classes}
            choices={choices}
            open={open}
            setOpen={setOpen}
            textButton={textButton}
            selectedItems={selectedItems}
            form={form}
            groupBy={groupBy}
            groupLabel={groupLabel}
            recipientId={recipientId}
            fundsData={fundsData}
            userProfile={userProfile}
          />
        </Grid>
        <Tooltip
          placement="bottom"
          classes={{
            tooltipPlacementBottom: classes.tooltipPlacementBottom
          }}
          title={
            'An email alias allows an investor to see a *reply to* address as a set of words rather than an email address. You can type any words as an Email Alias'
          }
        >
          <Grid item xs={3}>
            <TextInput
              source="alias"
              label="Alias:"
              type="text"
              helperText={false}
              options={{ autoComplete: 'off' }}
              variant={variant}
              multiple="multiple"
              disabled={overrideDisabled}
            />
          </Grid>
        </Tooltip>
        <Tooltip
          placement="bottom"
          classes={{
            tooltipPlacementBottom: classes.tooltipPlacementBottom
          }}
          title={
            'The Alias Reply To Address allows you to define any email address as the reply to address from your emails. The only valid entry in this form is an email address.'
          }
        >
          <Grid item xs={3}>
            <TextInput
              source="aliasTo"
              type="email"
              label="Alias Reply To:"
              helperText={false}
              options={{ autoComplete: 'off' }}
              variant={variant}
              validate={validateEmail}
              disabled={overrideDisabled}
            />
          </Grid>
        </Tooltip>
        <Grid item xs={3} className={classes.formCheckBox}>
          <div className={classes.checkboxDiv}>
            <Field
              name="rememberMe"
              source="rememberMe"
              component={CheckBoxField}
              label="Remember me"
              variant={variant}
              type="checkbox"
              disabled={overrideDisabled}
            />
          </div>
        </Grid>
        <ToField
          choices={choices}
          variant={variant}
          textButton={textButton}
          selectedItems={selectedItems}
          form={form}
          groupBy={groupBy}
          groupLabel={groupLabel}
          recipientId={recipientId}
          open={open}
          setOpen={setOpen}
          classes={classes}
        />
        <Grid item xs={12}>
          <TextInput
            source="cc"
            label="Cc:"
            type="text"
            parse={removeBlanks}
            helperText={false}
            options={{ autoComplete: 'off' }}
            variant={variant}
            validate={[fieldLengthValidator(4000), validateEmails]}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            source="bcc"
            label="Bcc:"
            type="text"
            parse={removeBlanks}
            helperText={false}
            options={{ autoComplete: 'off' }}
            variant={variant}
            validate={validateEmails}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            source="subject"
            validate={[required(), value => emptySubject(value)]}
            options={{
              autoComplete: 'off',
              label: (
                <FieldTitleWithListPopover
                  source="subject"
                  label="Subject:"
                  onSelectItem={handleSelectVariableSubject}
                  items={subjectVariables}
                  displayingVariables={variables}
                />
              )
            }}
            InputLabelProps={{
              classes: {
                root: classes.inputLabel
              }
            }}
            variant={variant}
            inputRef={subject}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12}>
          <RichTextInput
            source="msgBody"
            label="Email Content"
            validate={[required()]}
            variables={bodyVariables}
            displayingVariables={variables}
            ListPopoverProps={{
              buttonLabel: 'Insert Body Variable'
            }}
          />
        </Grid>
        {attachments && (
          <Grid item xs={12} className={classes.gridItem}>
            <FileInput
              source="attachments"
              label="Attachments"
              placeholder="Drag Files Here or Browse"
              resource="sendmailtolist"
              variant={variant}
              multiple={true}
              classes={{
                dropZone: classes.dropZone,
                removeButton: classes.removeButton
              }}
            >
              <FileField
                source="src"
                title="title"
                size="size"
                type="type"
                target="_blank"
              />
            </FileInput>
          </Grid>
        )}
      </Grid>
    );
  }
);

const mapDispatchToProps = {
  fetchStart,
  fetchEnd
};

export default connect(null, mapDispatchToProps)(EmailForm);
